import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import type {
  OpenResourceDrawerAction,
  UseResourceDrawerActionArgs,
} from '@ctw/shared/context/resource-drawer-context';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { SYSTEM_URLS } from '@zeus-health/types';
import { capitalize, compact } from 'lodash-es';

/** True when the MeasureReport uses the Payer Risk Gap profile. */
function isPayerRiskGapMeasureReport(model: RiskGapModel): boolean {
  return (
    model.resource.meta?.profile?.includes(SYSTEM_URLS.PROFILE.PAYER_RISK_GAP_MEASURE_REPORT) ??
    false
  );
}

/** Returns the Payer Risk Gap status from the MeasureReport extension, if present. */
function getPayerRiskGapStatus(model: RiskGapModel): string | undefined {
  return model.resource.extension?.find(
    (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.PAYER_RISK_GAP.STATUS,
  )?.valueString;
}

/** Returns the recapture flag (Y/N) from the Payer Risk Gap extension, if present. */
function getPayerRiskGapRecaptureFlag(model: RiskGapModel): string | undefined {
  const value = model.resource.extension?.find(
    (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.PAYER_RISK_GAP.RECAPTURE_FLAG,
  )?.valueString;
  if (value === '1') {
    return 'Y';
  }
  if (value === '0') {
    return 'N';
  }
  return undefined;
}

/** Builds the Payer Risk Gap drawer action (simplified read-only drawer). */
function payerRiskGapDrawerAction(model: RiskGapModel): OpenResourceDrawerAction {
  const payerStatus = getPayerRiskGapStatus(model);
  const recaptureFlag = getPayerRiskGapRecaptureFlag(model);
  const periodStart = model.resource.period?.start;
  const periodEnd = model.resource.period?.end;
  let periodDisplay = model.period;
  if (periodStart && periodEnd) {
    periodDisplay = `${formatFHIRDate(periodStart)} – ${formatFHIRDate(periodEnd)}`;
  }

  return {
    label: 'Payer Risk Gap',
    title: model.title || 'Payer Risk Gap',
    content: {
      body: (
        <div className={tw`space-y-2`}>
          <ContentCard variant="focal" title="Details">
            <DefinitionList
              className={tw`pt-2 pb-0`}
              items={compact([
                { label: 'HCC', value: `${model.hccCode}` },
                model.icd10Codes.length > 0
                  ? {
                      label: 'ICD-10',
                      value: (
                        <ul className={tw`flex flex-col gap-1`}>
                          {model.icd10Codes.map((code) => (
                            <li key={code.code}>
                              <span className={tw`font-medium`}>{code.code}</span>
                              {code.display && <span>&nbsp;{code.display}</span>}
                            </li>
                          ))}
                        </ul>
                      ),
                    }
                  : null,
                payerStatus ? { label: 'Status', value: capitalize(payerStatus) } : null,
                recaptureFlag !== undefined ? { label: 'Recapture', value: recaptureFlag } : null,
                periodDisplay ? { label: 'Period', value: periodDisplay } : null,
              ])}
            />
          </ContentCard>
        </div>
      ),
    },
  };
}

/**
 * True when this risk gap is the one already shown in the risk-gaps detail view (its id matches the
 * `detailResourceId` URL param). Opening a drawer for it would duplicate that detail view.
 */
function isRiskGapsDetailSubject(model: RiskGapModel): boolean {
  const detailResourceId = new URL(globalThis.location.href).searchParams.get('detailResourceId');
  return detailResourceId !== null && detailResourceId === model.id;
}

/**
 * Drawer action registered for the `MeasureReport` resource type. Payer risk gaps (their own
 * profile) open the simplified read-only drawer — including when reached via a MeasureReport deep
 * link — while legacy individual risk gaps resolve to no drawer. A payer gap that is already the
 * risk-gaps detail subject also resolves to no drawer, so a deep link carrying both the drawer and
 * detail params never duplicates that view. Returning `undefined` means no drawer opens; the
 * resource drawer context tolerates a nullish action result.
 */
export function measureReportDrawerAction(
  args: UseResourceDrawerActionArgs<'MeasureReport', RiskGapModel>,
): OpenResourceDrawerAction | undefined {
  if (!isPayerRiskGapMeasureReport(args.model) || isRiskGapsDetailSubject(args.model)) {
    return undefined;
  }
  return payerRiskGapDrawerAction(args.model);
}
